// pages/devolucionesCompra/DetalleDevolucionModal.tsx
import React from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Grid, Typography, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Paper,
  IconButton, Box, Button, Stack, Divider,
} from '@mui/material';
import { Close as CloseIcon, PlayCircleOutline as ProcessIcon } from '@mui/icons-material';

interface DetalleDevolucionModalProps {
  open: boolean;
  onClose: () => void;
  devolucion: any;
  dark?: boolean;
  onProcesar?: (id: number) => void;
  onRefresh?: () => void;
}

const P = {
  light: {
    bg: '#FFFFFF', bgS: '#F7F7F5', bgT: '#F1F1EF', text: '#37352F',
    textS: '#6B6B6B', textT: '#9B9A97', border: '#E9E9E7',
    success: '#0F7B6C', successBg: '#DBEDDB',
    warning: '#D9730D', warningBg: '#FADEC9',
    accent: '#2383E2',
  },
  dark: {
    bg: '#191919', bgS: '#202020', bgT: '#2B2B2B', text: '#E8E8E6',
    textS: '#9B9A97', textT: '#6B6B6B', border: '#333333',
    success: '#4DAB9A', successBg: '#1A2F2A',
    warning: '#C77D48', warningBg: '#2E2215',
    accent: '#529CCA',
  },
};

export const DetalleDevolucionModal: React.FC<DetalleDevolucionModalProps> = ({
  open, onClose, devolucion, dark = false, onProcesar, onRefresh,
}) => {
  const p = dark ? P.dark : P.light;

  if (!devolucion) return null;

  const esProcesada = devolucion.Estado === true;

  const estadoStyle = esProcesada
    ? { bg: p.successBg, color: p.success, label: 'Procesada' }
    : { bg: p.warningBg, color: p.warning, label: 'Pendiente' };

  const cellSx = {
    borderBottom: `1px solid ${p.border}`, color: p.text, fontSize: '0.875rem', py: 1.25,
  };
  const headCellSx = {
    ...cellSx,
    color: p.textT, fontWeight: 600, fontSize: '0.72rem',
    letterSpacing: '0.04em', textTransform: 'uppercase' as const,
    bgcolor: p.bgS,
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: { bgcolor: p.bg, color: p.text, borderRadius: '12px', border: `1px solid ${p.border}` },
      }}
    >
      <DialogTitle sx={{ pb: 1, borderBottom: `1px solid ${p.border}` }}>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Typography sx={{ fontWeight: 700, fontSize: '1rem', color: p.text }}>
              Devolución #{devolucion.Id}
            </Typography>
            <Box sx={{
              display: 'inline-flex', alignItems: 'center', gap: 0.75,
              px: 1.25, py: 0.3, borderRadius: '5px',
              bgcolor: estadoStyle.bg, color: estadoStyle.color,
              fontSize: '0.75rem', fontWeight: 600,
            }}>
              <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: estadoStyle.color, flexShrink: 0 }} />
              {estadoStyle.label}
            </Box>
          </Stack>
          <IconButton onClick={onClose} size="small" sx={{ color: p.textT }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ pt: 2.5 }}>
        {/* ── Info general ── */}
        <Box sx={{ mb: 3, p: 2, bgcolor: p.bgS, borderRadius: '8px', border: `1px solid ${p.border}` }}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <Typography sx={{ fontSize: '0.72rem', fontWeight: 600, color: p.textT, letterSpacing: '0.04em', textTransform: 'uppercase' }}>
                Orden de Compra
              </Typography>
              <Typography sx={{ fontWeight: 700, fontSize: '1rem', color: p.text, mt: 0.5 }}>
                OC-{devolucion.OrdenCompra}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography sx={{ fontSize: '0.72rem', fontWeight: 600, color: p.textT, letterSpacing: '0.04em', textTransform: 'uppercase' }}>
                Artículos devueltos
              </Typography>
              <Typography sx={{ fontWeight: 700, fontSize: '1rem', color: p.text, mt: 0.5 }}>
                {devolucion.detalles?.length ?? 0} artículo(s)
              </Typography>
            </Grid>
          </Grid>
        </Box>

        {/* ── Tabla de artículos ── */}
        <Typography sx={{ fontSize: '0.85rem', fontWeight: 600, color: p.textS, mb: 1.5 }}>
          Artículos devueltos
        </Typography>
        <TableContainer component={Paper} elevation={0} sx={{ border: `1px solid ${p.border}`, borderRadius: '8px', bgcolor: p.bg }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={headCellSx}>Artículo</TableCell>
                <TableCell sx={headCellSx} align="center">Cantidad</TableCell>
                <TableCell sx={headCellSx}>Motivo</TableCell>
                <TableCell sx={headCellSx}>Fecha</TableCell>
                <TableCell sx={headCellSx}>Observación</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {devolucion.detalles?.length > 0 ? (
                devolucion.detalles.map((detalle: any) => (
                  <TableRow key={detalle.Id} sx={{ '&:last-child td': { borderBottom: 'none' } }}>
                    <TableCell sx={cellSx}>
                      <Typography sx={{ fontWeight: 500, color: p.text, fontSize: '0.875rem' }}>
                        {detalle.articulo?.Nombre || 'N/A'}
                      </Typography>
                    </TableCell>
                    <TableCell sx={cellSx} align="center">
                      <Box sx={{
                        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                        width: 28, height: 28, borderRadius: '50%', bgcolor: p.bgT,
                        color: p.text, fontSize: '0.82rem', fontWeight: 600,
                      }}>
                        {detalle.Cantidad}
                      </Box>
                    </TableCell>
                    <TableCell sx={cellSx}>
                      <Typography sx={{ color: p.textS, fontSize: '0.875rem' }}>
                        {detalle.motivoDevolucion?.Nombre || 'N/A'}
                      </Typography>
                    </TableCell>
                    <TableCell sx={cellSx}>
                      <Typography sx={{ color: p.textS, fontSize: '0.875rem' }}>
                        {detalle.FechaDevolucion
                          ? new Date(detalle.FechaDevolucion).toLocaleDateString('es-CO')
                          : '—'}
                      </Typography>
                    </TableCell>
                    <TableCell sx={cellSx}>
                      <Typography sx={{ color: p.textT, fontSize: '0.85rem', fontStyle: detalle.Observacion ? 'normal' : 'italic' }}>
                        {detalle.Observacion || '—'}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={5} align="center" sx={{ ...cellSx, py: 4, color: p.textT }}>
                    Sin artículos registrados
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2, borderTop: `1px solid ${p.border}`, gap: 1 }}>
        <Button
          onClick={onClose}
          size="small"
          sx={{ color: p.textS, textTransform: 'none', fontWeight: 500, borderRadius: '7px' }}
        >
          Cerrar
        </Button>
        {!esProcesada && onProcesar && (
          <Button
            variant="contained"
            size="small"
            startIcon={<ProcessIcon />}
            onClick={() => onProcesar(devolucion.Id)}
            sx={{
              bgcolor: '#0F7B6C', color: '#fff', textTransform: 'none',
              fontWeight: 600, borderRadius: '7px', boxShadow: 'none',
              '&:hover': { bgcolor: '#0A5E52', boxShadow: 'none' },
            }}
          >
            Procesar devolución
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default DetalleDevolucionModal;
